import axios from "axios";
import { API_URL } from "constants";

export const updateCompany = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}`;
  return axios.patch(url, formData).then((response) => response.data);
};

// `areaId` optionally scopes the work-security document config (workSecurityRole*/
// workSecurityIncidentAct* + their *ConfigSource flags) returned in `companyEntity`
// to an instruction area (PR-2 §P5). Omitted — the byte-for-byte original request —
// keeps the company-wide config every other caller of this function relies on.
export const fetchById = async (companyId, areaId) => {
  const url = `${API_URL}/${companyId}${areaId ? `?areaId=${encodeURIComponent(areaId)}` : ""}`;
  return axios.get(url).then((response) => response.data);
};

// Master only; answers { secretExpiresAt: 'YYYY-MM-DD' | null }.
export const fetchMicrosoftSettings = async (companyId) => {
  const url = `${API_URL}/${companyId}/microsoft-settings`;
  return axios.get(url).then((response) => response.data);
};

export const updateMicrosoftSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/microsoft-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

// Master only; answers { ok: true } or { ok: false, reason } (never an error status for a failed check).
export const testMicrosoftConnection = async (companyId) => {
  const url = `${API_URL}/${companyId}/microsoft/test-connection`;
  return axios.post(url).then((response) => response.data);
};

export const updateFinvaldaSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/finvalda-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

// Reads from Finvalda with the saved login, nothing else. Always answers 200:
// { ok: true } or { ok: false, reason, message } (reasons in FinvaldaTab).
export const testFinvaldaConnection = async (companyId) => {
  const url = `${API_URL}/${companyId}/finvalda/test-connection`;
  return axios.post(url).then((response) => response.data);
};

export const updateDokobitSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/dokobit-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const updateFinanceSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/finance-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const updateBusinessTripSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/business-trip-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const updateOrdersSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/orders-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const updateFuelReportSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/fuel-report-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const updateAccountancyUserSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/accountancy-user-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const updateDirectorSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/director-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const updateBoardMemberSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/board-member-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

// PATCH { assetsResponsibleUser: userId | null } — null clears it. Master only.
export const updateAssetsResponsibleSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/onboarding-assets-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const updateInvoiceApproverSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/invoice-approver-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const updateTerminalDeliverySettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/terminal-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const updateDocumentPermitSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/document-permit-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const updateSignOnTabSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/signontab-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const updateSanctionsSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/sanctions-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const updateVatCheckSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/vat-check-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const updateOnboardingDocumentSettings = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/onboarding-document-settings`;
  return axios.patch(url, formData).then((response) => response.data);
};

// Logo printed at the top of the company's sales invoices. Master only.
// `logo` is `{ mime, dataUrl }`, or null when none has been uploaded.
export const fetchCompanyLogo = async (companyId) => {
  const url = `${API_URL}/${companyId}/settings/logo`;
  return axios.get(url).then((response) => response.data);
};

export const uploadCompanyLogo = async (companyId, dataUrl) => {
  const url = `${API_URL}/${companyId}/settings/logo`;
  return axios.put(url, { dataUrl }).then((response) => response.data);
};

export const deleteCompanyLogo = async (companyId) => {
  const url = `${API_URL}/${companyId}/settings/logo`;
  return axios.delete(url).then((response) => response.data);
};

// Journals excluded from turnover figures (debt/overdue still use every
// document — see pr3b-contract.md §3). Master only; the caller hides the
// section on a 403/404 rather than surfacing an error.
export const fetchLedgerJournals = async (companyId) => {
  const url = `${API_URL}/${companyId}/finvalda/ledger-journals`;
  return axios.get(url).then((response) => response.data);
};

export const updateLedgerJournals = async (companyId, excluded) => {
  const url = `${API_URL}/${companyId}/finvalda/ledger-journals`;
  return axios.patch(url, { excluded }).then((response) => response.data);
};